/**
 * @fileoverview Input/output schemas for the chapter-drafter-v2-neutral agent.
 *
 * The non-streaming `OutputSchema` is preserved so server-side composites
 * (chapter-drafter-enhanced-v2 → style-aligner-v2) and `executeAgent` callers
 * keep working unchanged. The streaming entry point emits `StreamChunk`s and
 * resolves to the same `Output` shape after the full text is parsed.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Context vars are kept aligned with chapter-drafter-v2's input schema so the
 * two drafters consume the same project data. The only deliberate omissions
 * are the style-bearing inputs (`styleGuide`, `writingStyleSample`): this
 * variant draws its prose rules from the static NEUTRAL PROSE STANDARDS
 * section of the template, deferring style to a downstream stylization pass.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.nextChapterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.scenes.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterPovCharacter.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.draftingGenreGuidance.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.loreContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.latestKnownPlotState.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.openPlotThreads.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.activeContinuityFacts.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.previousChapterTail.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.charactersCurrent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.worldElements.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.writingBlocklist.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
}).partial({
  scenes: true,
  nextChapterContext: true,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA (final, aggregated — same shape as before streaming)
// =============================================================================

export const OutputSchema = z.object({
  commentary: z.string().describe(
    'Brief commentary on the structural choices, beat allocation, and continuity decisions.'
  ),
  content: z.string().describe(
    'The fully written chapter in plain neutral prose, formatted as a single HTML string.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;

// =============================================================================
// SENTINELS (open-only, no closing markers)
// =============================================================================

/**
 * Open markers the model emits to delimit commentary and chapter content.
 * Open-only design: a transition is unambiguous, and there's nothing to
 * forget closing. The prefix is verbose on purpose — vanishingly unlikely
 * to collide with anything the model would emit in prose.
 */
export const SENTINELS = {
  commentary: '[[CHAPTER_DRAFTER::COMMENTARY]]',
  content: '[[CHAPTER_DRAFTER::CHAPTER_CONTENT]]',
} as const;

// =============================================================================
// STREAMING CHUNK SCHEMA
// =============================================================================

export const StreamChunkSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('commentary-delta'), delta: z.string() }),
  z.object({ type: z.literal('content-delta'),    delta: z.string() }),
  z.object({
    type: z.literal('done'),
    commentary: z.string(),
    content: z.string(),
    /** True if the parser had to fall back to recovery (e.g. missing markers). */
    recovered: z.boolean(),
  }),
  z.object({ type: z.literal('error'), message: z.string() }),
]);

export type StreamChunk = z.infer<typeof StreamChunkSchema>;

// =============================================================================
// SENTINEL PARSER
// =============================================================================

type ParserState = 'preamble' | 'commentary' | 'content';

export interface SentinelParser {
  /**
   * Feed a chunk of text. Returns deltas for whichever sections grew.
   * Buffers up to one marker length minus one across calls so a marker split
   * across chunks is still detected.
   */
  push(chunk: string): { commentaryDelta: string; contentDelta: string };
  /**
   * Drain remaining buffered text and return final state.
   * `recovered` is true when the input did not follow the expected
   * `[[…COMMENTARY]] … [[…CHAPTER_CONTENT]] …` shape and we had to guess.
   */
  finalize(): { commentary: string; content: string; recovered: boolean };
}

export function createSentinelParser(): SentinelParser {
  const OPEN_COMMENTARY = SENTINELS.commentary;
  const OPEN_CONTENT = SENTINELS.content;
  const MAX_MARKER = Math.max(OPEN_COMMENTARY.length, OPEN_CONTENT.length);

  let state: ParserState = 'preamble';
  let buffer = '';
  let commentary = '';
  let content = '';
  /**
   * Full preamble text held only for recovery — the search `buffer` is
   * capped to `MAX_MARKER - 1` chars while in preamble for efficiency, so we
   * can't reconstruct the full leading text from it. Cleared (and not used)
   * once we leave the preamble state.
   */
  let preambleAll = '';

  function trimLeadingNewline(s: string): string {
    if (s.startsWith('\r\n')) return s.slice(2);
    if (s.startsWith('\n')) return s.slice(1);
    return s;
  }

  function push(chunk: string): { commentaryDelta: string; contentDelta: string } {
    if (state === 'preamble') preambleAll += chunk;
    buffer += chunk;
    let commentaryDelta = '';
    let contentDelta = '';

    // A single chunk may cross multiple sentinel boundaries — loop until
    // the buffer can't be advanced further in the current state.
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (state === 'preamble') {
        const i = buffer.indexOf(OPEN_COMMENTARY);
        if (i === -1) {
          // Keep enough trailing chars that a marker split across this and
          // a future chunk is still findable. (preambleAll retains the rest
          // for recovery if no marker ever arrives.)
          if (buffer.length > MAX_MARKER - 1) {
            buffer = buffer.slice(-(MAX_MARKER - 1));
          }
          break;
        }
        buffer = trimLeadingNewline(buffer.slice(i + OPEN_COMMENTARY.length));
        state = 'commentary';
        preambleAll = '';
        continue;
      }

      if (state === 'commentary') {
        const i = buffer.indexOf(OPEN_CONTENT);
        if (i === -1) {
          // No transition yet — emit everything except a possible partial marker tail.
          const safeLen = Math.max(0, buffer.length - (MAX_MARKER - 1));
          if (safeLen > 0) {
            const piece = buffer.slice(0, safeLen);
            commentaryDelta += piece;
            commentary += piece;
            buffer = buffer.slice(safeLen);
          }
          break;
        }
        const piece = buffer.slice(0, i);
        commentaryDelta += piece;
        commentary += piece;
        buffer = trimLeadingNewline(buffer.slice(i + OPEN_CONTENT.length));
        state = 'content';
        continue;
      }

      // state === 'content' — everything left flows through.
      if (buffer.length > 0) {
        contentDelta += buffer;
        content += buffer;
        buffer = '';
      }
      break;
    }

    return { commentaryDelta, contentDelta };
  }

  function finalize(): { commentary: string; content: string; recovered: boolean } {
    let recovered = false;

    if (state === 'preamble') {
      // Marker never arrived. Treat the entire stream as content so prose isn't lost.
      content = preambleAll;
      buffer = '';
      preambleAll = '';
      recovered = true;
    } else if (state === 'commentary') {
      // Open commentary but never saw the content marker. Preserve what we
      // have as commentary; content stays empty.
      commentary += buffer;
      buffer = '';
      recovered = true;
    } else if (buffer.length > 0) {
      content += buffer;
      buffer = '';
    }

    return {
      commentary: commentary.trim(),
      content: content.trim(),
      recovered,
    };
  }

  return { push, finalize };
}
